import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Crown, Skull, ChevronLeft, Loader } from "lucide-react";
import { toast } from "sonner";

export default function Rebellion() {
  const { state, refreshState } = useAuth();
  const navigate = useNavigate();
  const [choice, setChoice] = useState(null); // "rebel" | "loyal"
  const [force, setForce] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!state) return null;

  const canRebel = state.can_rebel;
  const kingDefeated = state.king_defeated;

  const submit = async () => {
    if (!choice || !force) return;
    setBusy(true);
    try {
      const { data } = await api.post("/game/rebellion", {
        rebel: choice === "rebel",
        force_committed: force,
      });
      setResult(data.result);
      await refreshState();
      if (data.result.won) toast.success("THE WORLD CHANGES.");
      else toast.error("YOU OVERREACHED.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <NavBar active="rebellion" />

      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-16">
        <button onClick={() => navigate("/command")} className="btn-ghost mb-6 flex items-center gap-1" data-testid="rebellion-back-button">
          <ChevronLeft className="w-3 h-3" /> COMMAND
        </button>

        {kingDefeated ? (
          <div className="fade-up">
            <p className="tac-label">THE BONE THRONE // YOURS</p>
            <h1 className="font-serif-h text-5xl lg:text-7xl uppercase text-bone leading-none mt-3"
                data-testid="rebellion-throne-heading">
              You are King.
            </h1>
            <p className="mt-8 text-base text-[var(--text-main)] max-w-2xl leading-relaxed">
              The skull-and-marrow throne is yours. Take the world map. Conquer 25% of the globe to be crowned EMPEROR. Then, take the rest.
            </p>
            <button onClick={() => navigate("/world")} className="btn-blood mt-10" data-testid="rebellion-go-world-button">
              <Crown className="w-4 h-4" /> ENTER THE THEATRE
            </button>
          </div>
        ) : !canRebel ? (
          <div className="fade-up">
            <p className="tac-label">ACCESS DENIED</p>
            <h1 className="font-serif-h text-5xl uppercase text-bone leading-none mt-3"
                data-testid="rebellion-locked-heading">
              You are not yet a Crown Prince.
            </h1>
            <p className="mt-6 text-sm text-muted-h">Reach 3,001 zombies under your direct command.</p>
            <p className="mt-4 text-xs text-muted-h">Current rank: <span className="text-bone">{state.rank.name}</span> · Horde: <span className="text-bone">{state.personal_horde}</span></p>
          </div>
        ) : result ? (
          <div className="fade-up">
            <p className={`tac-label ${result.won ? "text-[#7FBA9D]" : "text-[var(--danger)]"}`}>
              {result.won ? "VICTORY" : "DEFEAT"}
            </p>
            <h1 className="font-serif-h text-5xl lg:text-7xl uppercase text-bone leading-none mt-3"
                data-testid="rebellion-result-heading">
              {result.scenario === "rebellion" && result.won ? "The Bone Throne falls."
                : result.scenario === "loyal_succession" && result.won ? "The pretenders torn."
                : "You overreached."}
            </h1>
            <div className="mt-10 narrative-panel">
              <p className="tac-label mb-3">THE OUTCOME</p>
              <p className="text-base lg:text-lg text-bone leading-relaxed" data-testid="rebellion-narrative">
                {result.narrative_summary}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-8">
              <Stat label="FALLEN" value={result.casualties} bad />
              <Stat label="CONVERTED" value={result.converted} good />
              <Stat label="HORDE" value={state.personal_horde} />
            </div>

            <button onClick={() => navigate("/command")} className="btn-blood mt-10" data-testid="rebellion-back-cmd-button">
              RETURN TO COMMAND
            </button>
          </div>
        ) : (
          <>
            <p className="tac-label fade-up">THE FORK // KNEEL OR BURN</p>
            <h1 className="font-serif-h text-5xl lg:text-7xl uppercase text-bone leading-none mt-3 fade-up"
                data-testid="rebellion-choice-heading">
              The King watches.<br />
              Three princes wait.
            </h1>
            <p className="mt-8 text-base text-[var(--text-main)] max-w-2xl leading-relaxed fade-up delay-1">
              The King has 50,000 hostages bricked into his fortress. His three Crown Princes circle each other.
              You are the fourth. Choose: rebel against the King and fight all four — or stay loyal and the
              other princes will rebel against YOU. Either way, the throne ends in someone's teeth.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12 fade-up delay-2">
              <ChoiceCard
                active={choice === "rebel"}
                onClick={() => setChoice("rebel")}
                title="REBEL"
                Icon={Skull}
                body="Storm the Bone Throne. Slaughter the King's hostages — half rise as your horde. The other three princes break their oaths. Highest reward. Highest risk."
                testid="choice-rebel"
              />
              <ChoiceCard
                active={choice === "loyal"}
                onClick={() => setChoice("loyal")}
                title="STAY LOYAL"
                Icon={Crown}
                body="Stand beside the King. The three pretenders rebel against him. Defend the throne, and the King names you sole heir. Safer. Slower. Cleaner."
                testid="choice-loyal"
              />
            </div>

            {choice && (
              <div className="mt-10 narrative-panel fade-up delay-3">
                <p className="tac-label mb-3">FORCE COMMITTED</p>
                <input type="range" min="1" max={Math.max(1, state.personal_horde)}
                       value={force || Math.floor(state.personal_horde * 0.7)}
                       onChange={(e) => setForce(Number(e.target.value))}
                       className="blood-slider"
                       data-testid="rebellion-force-slider" />
                <p className="text-sm text-bone mt-3" data-testid="rebellion-force-display">
                  {force || Math.floor(state.personal_horde * 0.7)} / {state.personal_horde} ZOMBIES
                </p>
                <p className="text-xs text-muted-h mt-2">
                  Anything held back stays in reserve. If you fall, anything committed is lost.
                </p>

                <button onClick={() => { if (!force) setForce(Math.floor(state.personal_horde * 0.7)); submit(); }}
                        disabled={busy} className="btn-blood mt-6" data-testid="rebellion-execute-button">
                  {busy ? <><Loader className="w-3 h-3 animate-spin" /> ...</> : "EXECUTE"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const ChoiceCard = ({ active, onClick, title, Icon, body, testid }) => (
  <button onClick={onClick}
          className={`tactical-card text-left p-6 transition-all ${active ? "border-blood bg-[#0F0707]" : ""}`}
          data-testid={testid}>
    <Icon className="w-5 h-5 text-blood mb-4" />
    <p className="font-serif-h text-2xl text-bone uppercase">{title}</p>
    <p className="text-sm text-[var(--text-main)] mt-3 leading-relaxed">{body}</p>
  </button>
);

const Stat = ({ label, value, good, bad }) => (
  <div className="tactical-card">
    <p className="tac-label">{label}</p>
    <p className={`font-mono text-lg mt-0.5 ${bad ? "text-[var(--danger)]" : good ? "text-[#7FBA9D]" : "text-bone"}`}>
      {value}
    </p>
  </div>
);
